/**
 * ServerAccess — dedicated page for managing per-tool WG access on
 * one MCP server. Cleaner real estate than the cramped popover/inline
 * surfaces we tried first; scales to 100s of WGs because the picker
 * is type-ahead, not a multi-select checkbox list.
 *
 * Route: /build/servers/:id/access
 *
 * Layout:
 *   - Header (server name + back link + summary)
 *   - "Apply to all tools" panel (bulk shortcut)
 *   - Per-tool table — each row has a chip-input cell that saves
 *     immediately on chip add/remove (optimistic UI; on error we
 *     surface a banner and the cache invalidates back to truth).
 */
import { useMemo, useState, type JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ListChecks, ShieldCheck, Box } from 'lucide-react';

import {
  ApiError,
  useServer,
  useToolsForServer,
  useUpdateTool,
  useWorkgroups,
} from '@/api/index.js';
import { ActionButton } from '@/components/console/ActionButton.js';
import { PageHeader } from '@/components/console/PageHeader.js';
import { SectionCard } from '@/components/console/SectionCard.js';
import { AsyncShell } from '@/components/console/StateBlocks.js';
import { WgChipsInput } from '@/components/console/WgChipsInput.js';

/**
 * Three-state authorization model (migration 0007 — closed by default):
 *   - `open`       → `open_to_all_wgs=true`. Anyone with any Stanford WG can call.
 *   - `restricted` → `open_to_all_wgs=false`, `restricted_to_wg_ids=[…]`. Only those WGs.
 *   - `closed`     → both empty. Nobody can call. Default state for new tools —
 *                    publisher must consciously pick one of the other two.
 */
type ToolMode = 'open' | 'restricted' | 'closed';
interface ToolDraft {
  mode: ToolMode;
  wgs: string[];
}
interface PersistedTool {
  id: string;
  name?: string;
  data_classification?: string;
  side_effect_level?: string;
  status?: string;
  restricted_to_wg_ids?: string[];
  open_to_all_wgs?: boolean;
}

export default function ServerAccess(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const server = useServer(id);
  const tools = useToolsForServer(id);
  const wgs = useWorkgroups({ limit: 500 });
  const updateTool = useUpdateTool();

  const [error, setError] = useState<string | null>(null);
  // Bulk-apply now picks one of the three modes for every tool at once.
  const [bulkMode, setBulkMode] = useState<ToolMode>('restricted');
  const [bulkDraft, setBulkDraft] = useState<string[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  // Per-tool draft state — chip add/remove + mode flip updates the draft
  // only, NOT the server. The user clicks Save (top of section) to commit
  // dirty rows. Map keyed by tool id; absent key = no edit, fall back to
  // the persisted state from the API.
  const [drafts, setDrafts] = useState<Record<string, ToolDraft>>({});
  const [savingDirty, setSavingDirty] = useState(false);

  const allWgIds = useMemo(() => (wgs.data?.items ?? []).map((w) => w.id), [wgs.data]);
  const toolItems = tools.data?.items ?? [];

  function reportError(e: unknown): void {
    setError(
      e instanceof ApiError ? `${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message,
    );
  }

  // Compute the persisted access state of a tool from the API row.
  function persistedDraft(t: PersistedTool): ToolDraft {
    if (t.open_to_all_wgs === true) return { mode: 'open', wgs: [] };
    const wgs = (t.restricted_to_wg_ids ?? []) as string[];
    if (wgs.length > 0) return { mode: 'restricted', wgs };
    return { mode: 'closed', wgs: [] };
  }

  // Effective draft (override if user edited, else persisted).
  function effectiveDraft(t: PersistedTool): ToolDraft {
    return drafts[t.id] ?? persistedDraft(t);
  }

  // Tools whose draft differs from the persisted state.
  const dirtyTools = useMemo(
    () =>
      toolItems.filter((row) => {
        const t = row as PersistedTool;
        const draft = drafts[t.id];
        if (!draft) return false;
        const p = persistedDraft(t);
        if (draft.mode !== p.mode) return true;
        if (draft.mode !== 'restricted') return false;
        if (draft.wgs.length !== p.wgs.length) return true;
        return draft.wgs.some((id, i) => id !== p.wgs[i]);
      }),
    [toolItems, drafts],
  );
  const dirtyCount = dirtyTools.length;

  function discardDrafts(): void {
    setDrafts({});
    setError(null);
  }

  // Translate a draft into the patch payload sent to PATCH /tools/:id.
  function patchFromDraft(d: ToolDraft): {
    open_to_all_wgs: boolean;
    restricted_to_wg_ids: string[];
  } {
    if (d.mode === 'open') return { open_to_all_wgs: true, restricted_to_wg_ids: [] };
    if (d.mode === 'restricted') return { open_to_all_wgs: false, restricted_to_wg_ids: d.wgs };
    // closed
    return { open_to_all_wgs: false, restricted_to_wg_ids: [] };
  }

  async function saveAllDirty(): Promise<void> {
    if (dirtyCount === 0) return;
    setError(null);
    setSavingDirty(true);
    try {
      for (const row of dirtyTools) {
        const t = row as PersistedTool;
        const draft = drafts[t.id];
        if (!draft) continue;
        await updateTool.mutateAsync({ id: t.id, patch: patchFromDraft(draft) });
      }
      setDrafts({});
    } catch (e) {
      reportError(e);
    } finally {
      setSavingDirty(false);
    }
  }

  async function applyBulk(): Promise<void> {
    if (toolItems.length === 0) return;
    const draft: ToolDraft =
      bulkMode === 'restricted'
        ? { mode: 'restricted', wgs: bulkDraft }
        : { mode: bulkMode, wgs: [] };
    const desc =
      bulkMode === 'open'
        ? `Open ALL ${toolItems.length} tools to every Stanford WG?`
        : bulkMode === 'closed'
          ? `Mark ALL ${toolItems.length} tools as "not granted" (no one can call them)?`
          : bulkDraft.length === 0
            ? `Restrict every tool to an empty WG list — that means nobody can call them. Continue?`
            : `Replace every tool's restriction with: ${bulkDraft.join(', ')}?`;
    if (!confirm(desc)) return;
    setError(null);
    setBulkBusy(true);
    try {
      for (const t of toolItems) {
        await updateTool.mutateAsync({
          id: (t as PersistedTool).id,
          patch: patchFromDraft(draft),
        });
      }
    } catch (e) {
      reportError(e);
    } finally {
      setBulkBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Publisher"
        title={server.data ? `${server.data.name} — Manage access` : 'Manage access'}
        description={
          server.data
            ? `${server.data.owningWgId} · ${toolItems.length} tool${toolItems.length === 1 ? '' : 's'} · risk: ${server.data.riskTier}`
            : 'Loading server…'
        }
      >
        {id && (
          <Link to={`/build/servers/${id}`}>
            <ActionButton icon={ArrowLeft}>Back to server</ActionButton>
          </Link>
        )}
      </PageHeader>

      <AsyncShell
        isLoading={server.isLoading || tools.isLoading || wgs.isLoading}
        isError={server.isError || tools.isError || wgs.isError}
        isEmpty={!server.data}
        error={server.error ?? tools.error ?? wgs.error}
        loadingTitle="Loading server + tools + WGs"
        emptyTitle="Server not found"
      >
        {server.data && (
          <>
            {/* Bulk apply panel — closed-by-default model */}
            <SectionCard
              icon={ShieldCheck}
              title="Apply to all tools"
              text="Sets every tool below to the same access state in one save. Closed-by-default: an empty WG list now means NOBODY can call the tool — pick 'Open to all' explicitly to grant universal access."
            >
              <ModeRadio value={bulkMode} onChange={setBulkMode} disabled={bulkBusy} />
              {bulkMode === 'restricted' && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    flexWrap: 'wrap',
                    marginTop: 10,
                  }}
                >
                  <div style={{ flex: '1 1 360px', minWidth: 0 }}>
                    <WgChipsInput
                      value={bulkDraft}
                      availableWgs={allWgIds}
                      onChange={setBulkDraft}
                      placeholder="type a WG to add…"
                      disabled={bulkBusy}
                    />
                  </div>
                </div>
              )}
              <div style={{ marginTop: 12 }}>
                <button
                  type="button"
                  onClick={() => void applyBulk()}
                  disabled={bulkBusy || toolItems.length === 0}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 8,
                    border: '1px solid #5fb27a',
                    background: 'rgba(95,178,122,0.18)',
                    color: '#1f5a37',
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: bulkBusy || toolItems.length === 0 ? 'not-allowed' : 'pointer',
                    opacity: bulkBusy ? 0.7 : 1,
                  }}
                >
                  {bulkBusy
                    ? `Applying to ${toolItems.length} tools…`
                    : `Apply to all ${toolItems.length} tools`}
                </button>
              </div>
            </SectionCard>

            {error && (
              <p
                role="alert"
                style={{
                  color: '#7a1f1f',
                  background: '#fff1f0',
                  border: '1px solid #e8b3b3',
                  padding: '8px 12px',
                  borderRadius: 8,
                  fontSize: 13,
                }}
              >
                {error}
              </p>
            )}

            {/* Per-tool access */}
            <SectionCard
              icon={Box}
              title="Per-tool access"
              text="Each row is one tool. Pick the access mode: Open to all, Restricted to specific WGs, or Closed (not yet granted). Closed-by-default — new tools start as Closed and nobody can call them until you make the call."
            >
              {/* Sticky save bar: only visible when there are unsaved changes. */}
              {dirtyCount > 0 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '8px 12px',
                    marginBottom: 10,
                    border: '1px solid #f5b700',
                    background: '#fef3c7',
                    color: '#92400e',
                    borderRadius: 8,
                    fontSize: 13,
                  }}
                  role="status"
                >
                  <span>
                    <strong>{dirtyCount}</strong> unsaved {dirtyCount === 1 ? 'change' : 'changes'}.
                  </span>
                  <span style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      onClick={discardDrafts}
                      disabled={savingDirty}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid var(--line-strong)',
                        background: 'rgba(255,255,255,0.85)',
                        borderRadius: 6,
                        fontSize: 13,
                        cursor: 'pointer',
                      }}
                    >
                      Discard
                    </button>
                    <button
                      type="button"
                      onClick={() => void saveAllDirty()}
                      disabled={savingDirty}
                      style={{
                        padding: '6px 14px',
                        border: '1px solid #5fb27a',
                        background: 'rgba(95,178,122,0.18)',
                        color: '#1f5a37',
                        borderRadius: 6,
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: savingDirty ? 'wait' : 'pointer',
                      }}
                    >
                      {savingDirty ? 'Saving…' : `Save ${dirtyCount}`}
                    </button>
                  </span>
                </div>
              )}

              {toolItems.length === 0 ? (
                <p className="subtle" style={{ fontSize: 13 }}>
                  No tools registered for this server yet.
                </p>
              ) : (
                <table
                  className="data-table"
                  style={{ fontSize: 13, width: '100%', tableLayout: 'fixed' }}
                >
                  <colgroup>
                    <col style={{ width: '32%' }} />
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Tool</th>
                      <th>WG access</th>
                    </tr>
                  </thead>
                  <tbody>
                    {toolItems.map((row) => {
                      const t = row as PersistedTool;
                      const draft = effectiveDraft(t);
                      const isDirty = drafts[t.id] !== undefined;
                      const setDraft = (next: ToolDraft): void =>
                        setDrafts((prev) => ({ ...prev, [t.id]: next }));
                      return (
                        <tr key={t.id} style={{ verticalAlign: 'top' }}>
                          <td style={{ padding: '12px 14px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <code style={{ fontSize: 13, fontWeight: 600 }}>{t.name ?? '—'}</code>
                              <span
                                style={{ fontSize: 11, color: '#6b7280' }}
                                title="data classification · side effect level · status"
                              >
                                {t.data_classification ?? '—'} · {t.side_effect_level ?? '—'} ·{' '}
                                {t.status ?? '—'}
                              </span>
                              {isDirty && (
                                <span
                                  style={{
                                    fontSize: 10,
                                    color: '#92400e',
                                    fontWeight: 700,
                                    letterSpacing: 0.04,
                                  }}
                                >
                                  ⏱ unsaved
                                </span>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '8px 14px' }}>
                            <ModeRadio
                              value={draft.mode}
                              onChange={(mode) =>
                                setDraft({ mode, wgs: mode === 'restricted' ? draft.wgs : [] })
                              }
                              disabled={savingDirty}
                              size="sm"
                            />
                            {draft.mode === 'restricted' && (
                              <div style={{ marginTop: 8 }}>
                                <WgChipsInput
                                  value={draft.wgs}
                                  availableWgs={allWgIds}
                                  onChange={(wgs) => setDraft({ mode: 'restricted', wgs })}
                                  placeholder="type a WG to restrict to…"
                                  disabled={savingDirty}
                                />
                              </div>
                            )}
                            {draft.mode === 'closed' && (
                              <p
                                className="subtle"
                                style={{ margin: '6px 0 0', fontSize: 11, color: '#7a1f1f' }}
                              >
                                ⚠ Nobody can call this tool until you grant access.
                              </p>
                            )}
                            {draft.mode === 'open' && (
                              <p
                                className="subtle"
                                style={{ margin: '6px 0 0', fontSize: 11, color: '#1f5a37' }}
                              >
                                ✓ Any Stanford user with at least one WG can call this.
                              </p>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </SectionCard>

            {/* Audit pointer */}
            <SectionCard
              icon={ListChecks}
              title="Audit"
              text="Each chip add / remove emits a tool.update audit event with before/after restricted_to_wg_ids."
            >
              <p className="subtle" style={{ fontSize: 13, margin: 0 }}>
                Open <Link to="/govern/audit">Govern → Audit</Link> and filter by{' '}
                <code>method=tool.update</code> to see the change history.
              </p>
            </SectionCard>
          </>
        )}
      </AsyncShell>
    </>
  );
}

/**
 * Three-state mode picker — Open / Restricted / Closed.
 * Used both per-row and in the bulk-apply panel so the choices stay in
 * one vocabulary.
 */
function ModeRadio({
  value,
  onChange,
  disabled,
  size,
}: {
  value: ToolMode;
  onChange: (next: ToolMode) => void;
  disabled?: boolean;
  size?: 'sm';
}): JSX.Element {
  const small = size === 'sm';
  const opts: { id: ToolMode; label: string; tone: 'green' | 'amber' | 'red'; hint: string }[] = [
    {
      id: 'open',
      label: 'Open to all',
      tone: 'green',
      hint: 'Any Stanford user with at least one WG.',
    },
    {
      id: 'restricted',
      label: 'Restricted to WGs',
      tone: 'amber',
      hint: 'Only members of the listed WGs.',
    },
    {
      id: 'closed',
      label: 'Closed (no access)',
      tone: 'red',
      hint: 'Nobody can call this tool.',
    },
  ];
  const colors: Record<'green' | 'amber' | 'red', { bg: string; border: string; fg: string }> = {
    green: { bg: 'rgba(95,178,122,0.18)', border: '#5fb27a', fg: '#1f5a37' },
    amber: { bg: 'rgba(245,183,0,0.20)', border: '#e8b13a', fg: '#92400e' },
    red: { bg: 'rgba(217,119,102,0.18)', border: '#d97766', fg: '#7a1f1f' },
  };
  return (
    <div
      role="radiogroup"
      aria-label="Tool access mode"
      style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}
    >
      {opts.map((o) => {
        const active = o.id === value;
        const c = colors[o.tone];
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange(o.id)}
            title={o.hint}
            style={{
              padding: small ? '4px 10px' : '6px 12px',
              fontSize: small ? 12 : 13,
              borderRadius: 999,
              border: `1px solid ${active ? c.border : 'var(--line-strong)'}`,
              background: active ? c.bg : 'transparent',
              color: active ? c.fg : 'inherit',
              fontWeight: active ? 700 : 500,
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1,
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
